import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import PhotoPicker from "../components/common/PhotoPicker";

export default function Register() {
  const { register, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Same "return to where the customer came from" pattern Login.jsx
  // already uses — lets the Checkout chooser's "Create Account" button
  // send a customer back to /quote (or anywhere else) after registering,
  // instead of always dropping them on /account.
  const from = location.state?.from?.pathname || "/account";

  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", confirmPassword: "" });
  const [photo, setPhoto] = useState(""); // "" = no photo (optional) — same data-URL shape PhotoPicker/CustomerRegisterIn.photo use
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      // confirmPassword is client-side validation only — the backend's
      // register payload has never accepted it and still doesn't, so it
      // is deliberately left out of this call.
      await register({ name: form.name, email: form.email, phone: form.phone, password: form.password, photo: photo || undefined });
      // Registration doesn't return a token — log in immediately after so
      // the customer isn't asked to type their password twice.
      await login(form.email, form.password);
      navigate(from, { replace: true });
    } catch (err) {
      if (!err?.response) {
        setError("Unable to connect to the server. Please check your connection and try again.");
      } else {
        const msg = err.response.data?.detail;
        setError(typeof msg === "string" ? msg : "Unable to register. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-sm mx-auto px-4 py-16">
      <h1 className="text-xl font-semibold text-neutral-900 mb-6 text-center">Create Account</h1>

      <form onSubmit={handleSubmit} className="space-y-4">
        <PhotoPicker name={form.name} photo={photo} onChange={setPhoto} disabled={submitting} />

        <div>
          <label htmlFor="reg-name" className="block text-sm font-medium text-neutral-700 mb-1">
            Full Name
          </label>
          <input
            id="reg-name"
            required
            autoComplete="name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
          />
        </div>
        <div>
          <label htmlFor="reg-email" className="block text-sm font-medium text-neutral-700 mb-1">
            Email
          </label>
          <input
            id="reg-email"
            type="email"
            required
            autoComplete="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
          />
        </div>
        <div>
          <label htmlFor="reg-phone" className="block text-sm font-medium text-neutral-700 mb-1">
            Phone
          </label>
          <input
            id="reg-phone"
            autoComplete="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
          />
        </div>
        <div>
          <label htmlFor="reg-password" className="block text-sm font-medium text-neutral-700 mb-1">
            Password
          </label>
          <input
            id="reg-password"
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
          />
          <p className="mt-1 text-xs text-neutral-400">At least 8 characters.</p>
        </div>
        <div>
          <label htmlFor="reg-confirm-password" className="block text-sm font-medium text-neutral-700 mb-1">
            Confirm Password
          </label>
          <input
            id="reg-confirm-password"
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={form.confirmPassword}
            onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
            className="w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
          />
          {form.confirmPassword && form.password !== form.confirmPassword && (
            <p className="mt-1 text-xs text-red-600">Passwords do not match.</p>
          )}
        </div>

        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-neutral-900 text-white text-sm font-medium rounded-lg px-4 py-3 hover:bg-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? "Creating account..." : "Create Account"}
        </button>
      </form>

      <p className="text-sm text-neutral-500 text-center mt-6">
        Already have an account?{" "}
        <Link to="/login" state={location.state} className="text-neutral-900 underline">
          Login
        </Link>
      </p>
    </div>
  );
}
